'use client';

import { c as _c } from "react/compiler-runtime";
import type { Globe } from 'cobe';
import createGlobe from 'cobe';
import { MapPinIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge } from '../ui/badge';
const MARKERS = [{
  id: 'hk',
  location: [22.3193, 114.1694] as [number, number],
  size: 0.03
}];
function getThemeOptions(isDark: boolean) {
  return {
    dark: isDark ? 1 : 0,
    diffuse: isDark ? 2 : 1.5,
    mapBrightness: isDark ? 2 : 1.5,
    baseColor: isDark ? [0.8, 0.8, 0.8] as [number, number, number] : [1, 1, 1] as [number, number, number],
    markerColor: isDark ? [1, 1, 1] as [number, number, number] : [0, 0, 0] as [number, number, number],
    glowColor: isDark ? [0.5, 0.5, 0.5] as [number, number, number] : [0.94, 0.93, 0.91] as [number, number, number]
  };
}
export function LocationCard() {
  const $ = _c(23);
  const canvasRef = useRef(null);
  const globeRef = useRef(null);
  const [ready, setReady] = useState(false);
  const pointerInteractingRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      phi: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dragOffsetRef = useRef(t0);
  const phiOffsetRef = useRef(2.75);
  const t = useTranslations();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = e => {
      pointerInteractingRef.current = {
        x: e.clientX,
        y: e.clientY
      };
      if (canvasRef.current) {
        canvasRef.current.style.cursor = "grabbing";
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handlePointerDown = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const handlePointerMove = function handlePointerMove(e_0) {
        if (pointerInteractingRef.current !== null) {
          const deltaX = e_0.clientX - pointerInteractingRef.current.x;
          dragOffsetRef.current = {
            phi: deltaX / 300
          };
        }
      };
      const handlePointerUp = function handlePointerUp() {
        if (pointerInteractingRef.current !== null) {
          phiOffsetRef.current = phiOffsetRef.current + dragOffsetRef.current.phi;
          dragOffsetRef.current = {
            phi: 0
          };
        }
        pointerInteractingRef.current = null;
        if (canvasRef.current) {
          canvasRef.current.style.cursor = "grab";
        }
      };
      globalThis.addEventListener("pointermove", handlePointerMove, {
        passive: true
      });
      globalThis.addEventListener("pointerup", handlePointerUp, {
        passive: true
      });
      return () => {
        globalThis.removeEventListener("pointermove", handlePointerMove);
        globalThis.removeEventListener("pointerup", handlePointerUp);
      };
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!canvasRef.current) {
        return;
      }
      const canvas = canvasRef.current;
      const width = canvas.offsetWidth;
      const dpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 640 ? 1.8 : 2);
      globeRef.current = createGlobe(canvas, {
        devicePixelRatio: dpr,
        width,
        height: width,
        phi: phiOffsetRef.current,
        theta: 0,
        mapSamples: 16000,
        markerElevation: 0.01,
        markers: MARKERS,
        ...getThemeOptions(false)
      });
      let animationId = 0;
      const resizeObserver = new ResizeObserver(entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        const nextWidth = Math.round(entry.contentRect.width);
        const nextDpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 640 ? 1.8 : 2);
        globeRef.current?.update({
          devicePixelRatio: nextDpr,
          width: nextWidth,
          height: nextWidth
        });
      });
      resizeObserver.observe(canvas);
      function animate() {
        globeRef.current?.update({
          phi: phiOffsetRef.current + dragOffsetRef.current.phi
        });
        animationId = requestAnimationFrame(animate);
      }
      animate();
      const fadeInId = requestAnimationFrame(() => {
        setReady(true);
      });
      return () => {
        cancelAnimationFrame(animationId);
        cancelAnimationFrame(fadeInId);
        resizeObserver.disconnect();
        globeRef.current?.destroy();
        globeRef.current = null;
      };
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const observer = new MutationObserver(() => {
        const isDark = document.documentElement.classList.contains("dark");
        globeRef.current?.update(getThemeOptions(isDark));
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"]
      });
      return () => {
        observer.disconnect();
      };
    };
    t7 = [];
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <MapPinIcon className="size-4.5" />;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t) {
    t9 = t("homepage.about-me.location");
    $[9] = t;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t9) {
    t10 = <div className="flex items-center gap-2">{t8}<h2 className="text-sm">{t9}</h2></div>;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== ready) {
    t11 = <canvas ref={canvasRef} onPointerDown={handlePointerDown} data-ready={ready} className="aspect-square size-full cursor-grab touch-none opacity-0 transition-opacity data-[ready=true]:opacity-100" />;
    $[13] = ready;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t) {
    t12 = MARKERS.map(m => <div key={m.id} className="pointer-events-none absolute bottom-[anchor(top)] left-[anchor(center)] -translate-x-1/2 -translate-y-2 transition-[opacity,filter]" style={{
      positionAnchor: `--cobe-${m.id}`,
      opacity: `var(--cobe-visible-${m.id}, 0)`,
      filter: `blur(calc((1 - var(--cobe-visible-${m.id}, 0)) * 8px))`
    }}><Badge>{t("homepage.about-me.location-value")}</Badge></div>);
    $[15] = t;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t11 || $[18] !== t12) {
    t13 = <div className="relative mx-auto -mt-4 aspect-square max-w-125 select-none">{t11}{t12}</div>;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t10 || $[21] !== t13) {
    t14 = <div className="relative flex h-60 flex-col gap-6 overflow-hidden rounded-2xl p-4 shadow-feature-card lg:p-6">{t10}{t13}</div>;
    $[20] = t10;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
}